import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Shield, MagnifyingGlass, CaretRight, ChartLine, Lock, Buildings, Scales, CurrencyBtc, Detective, ChartBar, ArrowRight, Lightning, ShoppingBag, CreditCard, Gavel } from '@phosphor-icons/react';
import AuthModal from '../components/AuthModal';

export default function Home() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const publicUrl = process.env.PUBLIC_URL;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">
            {/* Left - Heading + subtitle (3 cols) */}
            <div className="lg:col-span-3">
              <h1 className="font-heading text-[2.75rem] sm:text-[3.5rem] lg:text-[4.25rem] font-bold tracking-tight text-white leading-[1.1] mb-6" data-testid="hero-heading">
                Crystal intelligence for crime detection
              </h1>
              <p className="text-base text-slate-400 leading-relaxed max-w-xl" data-testid="hero-subheading">
                Advanced blockchain analytics for crypto crime investigation, compliance, and risk management
              </p>
            </div>

            {/* Right - Description + CTA (2 cols) */}
            <div className="lg:col-span-2 lg:pt-6">
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                Uncover illicit finance, trace crypto movements, and enforce compliance with AI-powered blockchain analytics
              </p>
              <Button
                size="lg"
                onClick={() => setShowAuthModal(true)}
                className="bg-[#F5E642] hover:bg-[#E8D93A] text-black font-semibold h-12 px-8 rounded-lg text-base"
                data-testid="get-started-cta"
              >
                Start Now
                <ArrowRight className="ml-2" weight="bold" size={18} />
              </Button>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-20">
            {/* Card 1 - Investigate */}
            <Card className="bg-card/60 border-border/50 rounded-2xl overflow-hidden p-0">
              {/* Visual: Transaction flow diagram */}
              <div className="p-8 pb-4">
                <div className="bg-muted/30 rounded-xl p-6 relative min-h-[220px]">
                  {/* Flow diagram */}
                  <div className="flex items-center justify-between">
                    {/* Exchange node */}
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 rounded-full border-2 border-emerald-400/40 bg-emerald-400/5 flex items-center justify-center">
                        <CreditCard size={22} className="text-slate-300" />
                      </div>
                      <span className="text-xs text-emerald-400 mt-2 font-medium">Exchange</span>
                    </div>

                    {/* Middle connections */}
                    <div className="flex-1 flex flex-col items-center gap-6 px-4">
                      {/* Top flow */}
                      <div className="flex items-center w-full">
                        <div className="flex-1 h-px bg-slate-600"></div>
                        <div className="px-3 py-1 rounded-full bg-muted border border-border flex items-center space-x-1.5 mx-2">
                          <svg className="w-3.5 h-3.5 text-blue-400" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z"/></svg>
                          <span className="text-xs text-slate-300 font-mono">1,657.17</span>
                        </div>
                        <div className="flex-1 h-px bg-slate-600"></div>
                      </div>
                      {/* Bottom flow */}
                      <div className="flex items-center w-full">
                        <div className="flex-1 h-px bg-slate-600"></div>
                        <div className="px-3 py-1 rounded-full bg-muted border border-border flex items-center space-x-1.5 mx-2">
                          <CurrencyBtc size={14} className="text-orange-400" />
                          <span className="text-xs text-slate-300 font-mono">1,753.24</span>
                        </div>
                        <div className="flex-1 h-px bg-slate-600"></div>
                      </div>
                    </div>

                    {/* Right nodes */}
                    <div className="flex flex-col items-center gap-6">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                          <Lock size={16} className="text-yellow-400" />
                        </div>
                        <span className="text-[10px] text-slate-500 mt-1 font-mono">0xa73...4a30</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-red-400/10 border border-red-400/30 flex items-center justify-center">
                          <Shield size={16} className="text-red-400" />
                        </div>
                        <span className="text-[10px] text-slate-500 mt-1 font-mono">37Nv...ZLfSP</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-8 pb-8">
                <h3 className="font-heading text-xl font-bold text-white mb-2">Investigate crypto crime in seconds</h3>
                <p className="text-sm text-slate-400">Trace wallets & visualize transaction flows with interactive network maps</p>
              </div>
            </Card>

            {/* Card 2 - Monitor */}
            <Card className="bg-card/60 border-border/50 rounded-2xl overflow-hidden p-0">
              <div className="p-8 pb-4">
                <div className="bg-muted/30 rounded-xl p-5 grid grid-cols-5 gap-4 min-h-[220px]">
                  {/* Left: Monitor Transfers table */}
                  <div className="col-span-3 space-y-3">
                    <p className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-3">Monitor Transfers</p>
                    {[
                      { name: 'Darknet Kraken', icon: <ShoppingBag size={14} />, pct: '100%', color: 'text-red-400' },
                      { name: 'Transit Swap', icon: <CreditCard size={14} />, pct: '60%', color: 'text-orange-400' },
                      { name: 'ChipMixer.com', icon: <Gavel size={14} />, pct: '75%', color: 'text-yellow-400' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                          <span className="text-xs text-blue-400 font-medium">{item.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-slate-500">{item.icon}</span>
                          <Lightning size={12} className={item.color} weight="fill" />
                          <span className="text-xs font-semibold text-slate-300">{item.pct}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Right: Bar chart */}
                  <div className="col-span-2">
                    <p className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-3">Transfers</p>
                    <div className="flex items-end justify-between h-32 gap-1 px-1">
                      {[40, 65, 30, 80, 55, 90, 45, 70, 35, 85, 60, 50, 75, 40, 95, 55].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-blue-500/70 rounded-t-sm min-w-[3px]"
                          style={{ height: `${h}%` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-8 pb-8">
                <h3 className="font-heading text-xl font-bold text-white mb-2">Monitor risk & classify entities</h3>
                <p className="text-sm text-slate-400">Flag risky flows instantly and prioritize threats</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

{/* Trusted by Global Companies */}
<section
  className="py-16 lg:py-24 bg-navy-900]"
  style={{
    backgroundImage:
      "radial-gradient(circle at 1px 1px, rgba(4, 11, 32, 0.08) 1px, transparent 0px)",
    backgroundSize: "20px 20px",
  }}
  data-testid="trusted-section"
>
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <h2 className="text-center font-heading text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-white mb-16 tracking-tight">
      Trusted by global companies
    </h2>

    {/* Row 1 — 3 logos */}
    <div className="flex flex-wrap justify-center items-center gap-x-20 lg:gap-x-28 gap-y-10 mb-14">
      <img src={`${publicUrl}/logos/Galaxy.svg`} alt="Galaxy" className="h-12 sm:h-14 lg:h-16 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
      <img src={`${publicUrl}/logos/B2Broker.svg`} alt="B2Broker" className="h-12 sm:h-14 lg:h-16 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
      <img src={`${publicUrl}/logos/CyberPolice.svg`} alt="Cyber Police" className="h-14 sm:h-16 lg:h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
    </div>

    {/* Row 2 — 5 logos */}
    <div className="flex flex-wrap justify-center items-center gap-x-14 lg:gap-x-20 gap-y-10">
      <img src={`${publicUrl}/logos/MagnetiqBank.svg`} alt="Magnetiq Bank" className="h-10 sm:h-12 lg:h-14 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
      <img src={`${publicUrl}/logos/ClearJunction.svg`} alt="Clear Junction" className="h-12 sm:h-14 lg:h-16 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
      <img src={`${publicUrl}/logos/CoinsPaid.svg`} alt="CoinsPaid" className="h-11 sm:h-13 lg:h-15 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
      <img src={`${publicUrl}/logos/ChangeNOW.svg`} alt="ChangeNOW" className="h-12 sm:h-14 lg:h-16 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
      <img src={`${publicUrl}/logos/WhiteBIT.svg`} alt="WhiteBIT" className="h-11 sm:h-13 lg:h-15 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300" />
    </div>
  </div>
</section>

{/* Stats Section */}
<section
  className="py-16 lg:py-20"
  style={{
    backgroundImage:
      "radial-gradient(circle at 1px 1px, rgba(0, 102, 255, 0.08) 1px, transparent 0px)",
    backgroundSize: "20px 20px",
  }}
>
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
      {[
        { number: '30M+', label: 'Transfers Flagged' },
        { number: '100K+', label: 'Verified Entities' },
        { number: '210M+', label: 'Verified Transfers' },
        { number: '99.9%', label: 'Platform Uptime' },
      ].map((stat, idx) => (
        <div key={idx} className="text-center" data-testid={`stat-${idx}`}>
          <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0066FF] mb-2">{stat.number}</p>
          <p className="text-sm text-slate-400">{stat.label}</p>
        </div>
      ))}
    </div>
  </div>
</section>

      {/* Protecting the future - Mission Statement */}
      <section className="py-16 lg:py-24 bg-card/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-6 leading-relaxed">
            Protecting the future of global financial growth with advanced{' '}
            <span className="text-[#0066FF]">blockchain analytics</span>{' '}
            and risk management solutions.
          </h2>
        </div>
      </section>

      {/* Who We Serve - Use Cases */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Buildings size={36} weight="duotone" />,
                title: 'Banks & Payment Providers',
                items: [
                  'Flag crypto risks in real time',
                  'Accelerate KYC with onchain data',
                  'Automate compliance at scale'
                ],
              },
              {
                icon: <Scales size={36} weight="duotone" />,
                title: 'Governments & Law Enforcement',
                items: [
                  'Trace illicit crypto networks',
                  'Disrupt fraud and trafficking',
                  'Generate actionable threat alerts'
                ],
              },
              {
                icon: <CurrencyBtc size={36} weight="duotone" />,
                title: 'Digital Assets & Crypto Exchanges',
                items: [
                  'Simplify regulatory compliance',
                  'Detect and block fraud threats',
                  'Strengthen platform security'
                ],
              },
            ].map((useCase, idx) => (
              <Card
                key={idx}
                className="p-8 bg-card border-border hover:border-[#0066FF]/30 transition-all duration-300 group"
                data-testid={`use-case-${idx}`}
              >
                <div className="text-[#0066FF] mb-5 group-hover:scale-110 transition-transform duration-300">{useCase.icon}</div>
                <h3 className="font-heading text-lg font-medium text-white mb-4">{useCase.title}</h3>
                <ul className="space-y-3">
                  {useCase.items.map((item, i) => (
                    <li key={i} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-2 flex-shrink-0"></div>
                      <span className="text-sm text-slate-400">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 lg:py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4">
              Our Solutions
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              Comprehensive blockchain intelligence solutions designed for security, compliance, and asset protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <MagnifyingGlass size={28} weight="duotone" />,
                title: 'Visual Investigations',
                description: 'Trace suspicious wallets across chains and visualize transaction flows with interactive network maps.',
              },
              {
                icon: <Shield size={28} weight="duotone" />,
                title: '360 Risk Analysis',
                description: 'Identify risky wallets and entities. Access verified data and evaluate exposure to sanctions.',
              },
              {
                icon: <ChartBar size={28} weight="duotone" />,
                title: 'Real-time Monitoring',
                description: 'Instant alerts on suspicious activity. Track live transactions and integrate compliance alerts.',
              },
              {
                icon: <Detective size={28} weight="duotone" />,
                title: 'Fraud Detection',
                description: 'Identify patterns of fraudulent behavior and prevent future attacks with AI-powered analytics.',
              },
            ].map((service, idx) => (
              <Card
                key={idx}
                className="p-6 bg-card border-border hover:border-[#0066FF]/30 transition-all duration-300 group"
                data-testid={`service-card-${idx}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#0066FF]/10 flex items-center justify-center text-[#0066FF] mb-4 group-hover:bg-[#0066FF]/20 transition-colors duration-300">{service.icon}</div>
                <h3 className="font-heading text-base font-medium text-white mb-2">{service.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-8 left-1/6 right-1/6 h-px bg-gradient-to-r from-transparent via-[#0066FF]/30 to-transparent"></div>
            
            {[
              {
                step: '01',
                title: 'Report Your Case',
                description: 'Submit details about your lost or stolen assets through our secure portal.',
              },
              {
                step: '02',
                title: 'Investigation',
                description: 'Our experts analyze blockchain data and trace asset movements across chains.',
              },
              {
                step: '03',
                title: 'Protection',
                description: 'We work with authorities and exchanges to trace and protect your digital assets.',
              },
            ].map((item, idx) => (
              <div key={idx} className="text-center relative" data-testid={`how-it-works-step-${idx}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#0066FF]/10 border border-[#0066FF]/20 mb-6 relative z-10">
                  <span className="font-heading text-xl font-bold text-[#0066FF]">{item.step}</span>
                </div>
                <h3 className="font-heading text-lg font-medium text-white mb-3">{item.title}</h3>
                <p className="text-sm text-slate-400 max-w-xs mx-auto">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4">
              Client Success Stories
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Daniel Johnson',
                role: 'CEO, TechCorp',
                image: 'https://images.unsplash.com/photo-1629507208649-70919ca33793?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTIwODEwOXww&ixlib=rb-4.1.0&q=85',
                quote: 'Crystal Intelligence helped us trace $500K in stolen crypto. Their expertise is unmatched in the industry.',
              },
              {
                name: 'Susan Chen',
                role: 'Investor',
                image: 'https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTIwODEwOXww&ixlib=rb-4.1.0&q=85',
                quote: 'Professional, thorough, and results-driven. They traced my assets across multiple blockchains.',
              },
              {
                name: 'Carl Andersen',
                role: 'CFO, FinanceHub',
                image: 'https://images.unsplash.com/photo-1762522927402-f390672558d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTIwODEwOXww&ixlib=rb-4.1.0&q=85',
                quote: 'Outstanding service. They provided detailed forensic analysis that was crucial for our investigation.',
              },
            ].map((testimonial, idx) => (
              <Card key={idx} className="p-6 bg-card border-border" data-testid={`testimonial-${idx}`}>
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-slate-300 mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover mr-3"
                  />
                  <div>
                    <div className="text-sm font-medium text-white">{testimonial.name}</div>
                    <div className="text-xs text-slate-400">{testimonial.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl bg-gradient-to-r from-[#0066FF]/10 to-[#0066FF]/5 border border-[#0066FF]/20 p-12 text-center overflow-hidden">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,102,255,0.06) 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }}></div>
            <div className="relative z-10">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4">
                Ready to Protect Your Assets?
              </h2>
              <p className="text-base text-slate-400 mb-8 max-w-lg mx-auto">
                Don't let stolen crypto disappear. Our team is ready to help you trace and protect your digital assets.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/report-case">
                  <Button size="lg" className="bg-[#0066FF] hover:bg-[#0052CC] h-12 px-8" data-testid="footer-report-cta">
                    Report Your Case Now
                    <CaretRight className="ml-2" weight="bold" size={16} />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => setShowAuthModal(true)}
                  className="h-12 px-8 border-slate-700 hover:bg-slate-800"
                >
                  Get Financial Expert
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
